// src/app/hooks/queries.ts
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  useGetAccessoriesQuery,
  useGetAhuRatedVa120Query,
  useGetAhuRatedVa24Query,
  useGetBlankOffQuery,
  useGetComponentsQuery,
  useGetConstructionClassesQuery,
  useGetConstructionMethodQuery,
  useGetDrainPanAvaibleSectionsQuery,
  useGetIntWallPlanesQuery,
  useGetPricingMethodsQuery,
  useGetUvcAvaibleSectionsQuery,
  useGetVoltagesQuery,
  useLazyGetAccessoryPositionQuery,
  useLazyGetAhuByIdQuery,
  useLazyGetAhuCertificationsQuery,
  useLazyGetAhuComponentsQuery,
  useLazyGetAhuParamsQuery,
  useLazyGetAhuTunnelsQuery,
  useLazyGetDMountsQuery,
  useLazyGetHorizontalInternalWallsQuery,
  useLazyGetRoofLayersQuery,
  useLazyGetRoofShapesQuery,
  useLazyGetRoofSlopeDirectionQuery,
  useLazyGetRoofSlopeOrinetationQuery,
  useLazyGetSectionAccessoriesQuery,
  useLazyGetSpecialOptionsQuery,
  useLazyGetSpecialOptionsSettingsQuery,
  useLazyGetTunnelCasingQuery,
  useLazyGetTunnelFloorQuery,
  useLazyGetTunnelIndexesQuery,
  useLazyGetTunnelSectionsQuery,
  useLazyGetVerticalInternalWallsQuery,
} from '../../services';
import {
  setAccessories,
  setAccessoriesAvaibleSections,
  setAccessoryPosition,
  setAhu,
  setAhuCertifications,
  setAhuComponents,
  setAhuParams,
  setAhuTunnels,
  setCurrentProjectId,
  setComponents,
  setDmounts,
  setInternalWalls,
  setRatedVA,
  setRoofDirections,
  setRoofLayers,
  setRoofOrientation,
  setRoofSeamDirections,
  setRoofShapes,
  setSectionAccessories,
  setSelectedSection,
  setSpecialOptions,
  setSpecialOptionsSettings,
  setTunnelCasing,
  setTunnelFloor,
  setTunnelSections,
  setVoltages,
  setConstructionClasses,
  setTunnelsIndexes,
} from '../../slices';
import {
  useGetProjectAhus,
  useGetProjectById,
  useGetProjectTimeline,
  useGetTaskStatuses,
  useGetTasks,
} from '../project';
import { IAHUParams } from '../../models';
import type { IState } from '../../../../models';
import { subscribeToEvent } from '../../services/kaiSocket';

export const useGetAhuMainData = (ahu_id: string) => {
  const { params } = useAhuParams();
  const { ahu } = useGetAhuById(ahu_id);
  const { project } = useGetProjectById(ahu?.project_id);
  const { timeline } = useGetProjectTimeline(project?.id);
  const { ahus } = useGetProjectAhus(project?.id as number);
  useGetAccessoriesAvaibleSections();

  return {
    loading: !params || !ahu || !project || !timeline || !ahus,
    ahu,
    project,
  };
};

export const useGetAhuData = (ahu_id: string) => {
  const [
    { tunnels },
    { taskStatuses },
    { tasks },
    { voltages },
    { certifications },
    { roofLayers },
    { roofShapes },
    { roofSeamDirections },
    { options },
    { settings },
    { components },
    { rated120, rated24 },
  ] = [
    useGetAhuTunnels(+ahu_id),
    useGetTaskStatuses(),
    useGetTasks(),
    useGetVoltages(),
    useGetAhuCertifications(+ahu_id),
    useGetRoofLayers(),
    useGetRoofShapes(),
    useGetRoofSeamDirections(),
    useGetSpecialOptions(ahu_id),
    useGetSpecialOptionsSettings(ahu_id),
    useGetAhuComponents(+ahu_id),
    useGetRatedVa(+ahu_id),
    // useGetCriticalTasks(),
  ];

  const loading = useMemo(() => {
    return (
      !tunnels ||
      !taskStatuses ||
      !tasks ||
      !certifications ||
      !roofLayers ||
      !roofShapes ||
      !roofSeamDirections ||
      !options ||
      !settings ||
      !components ||
      !rated120 ||
      !rated24
    );
  }, [
    tunnels,
    taskStatuses,
    tasks,
    voltages,
    certifications,
    roofLayers,
    roofShapes,
    roofSeamDirections,
    options,
    components,
    settings,
    rated24,
    rated120,
  ]);
  return { loading };
};

export const useAhuExtraData = (
  construction_class_id: number,
  includeExtraData: boolean,
) => {
  const constructionMethodQuery = useGetConstructionMethodQuery(
    construction_class_id,
    { skip: !includeExtraData },
  );

  const blankOffQuery = useGetBlankOffQuery(undefined, {
    skip: !includeExtraData,
  });

  const intWallPlansQuery = useGetIntWallPlanesQuery(undefined, {
    skip: !includeExtraData,
  });

  const pricingMethodsQuery = useGetPricingMethodsQuery(undefined, {
    skip: !includeExtraData,
  });

  const data = {
    construction_method: constructionMethodQuery.data,
    blank_off: blankOffQuery.data,
    int_wall_plans: intWallPlansQuery.data,
    pricing_methods: pricingMethodsQuery.data,
  };

  const ready = !includeExtraData || Object.values(data).every(Boolean);

  const merge = useCallback(
    (params: IAHUParams): IAHUParams =>
      includeExtraData && ready
        ? ({ ...params, ...data } as IAHUParams)
        : params,
    [includeExtraData, ready, data],
  );

  return {
    extraData: data,
    ready,
    merge,
  };
};

export const useAhuSocketParams = (
  mergeParams?: (p: IAHUParams) => IAHUParams,
) => {
  const dispatch = useDispatch();
  const [getAhuParams] = useLazyGetAhuParamsQuery();

  const [isLoading, setIsLoading] = useState(false);
  const requestedCcIdRef = useRef<number | null>(null);

  const SOCKET_EVENT = 'data';
  const AHU_PARAMS_SOCKET_NAME = 'cms_data';

  useEffect(() => {
    const unsubscribe = subscribeToEvent(SOCKET_EVENT, (msg: any) => {
      const data = msg?.data ?? msg;

      if (
        data?.name !== AHU_PARAMS_SOCKET_NAME ||
        data?.construction_class_id !== requestedCcIdRef.current
      ) {
        return;
      }

      const params = mergeParams ? mergeParams(data.data) : data.data;

      dispatch(setAhuParams(params));

      setIsLoading(false);
      requestedCcIdRef.current = null;
    });

    return unsubscribe;
  }, [subscribeToEvent, dispatch, mergeParams]);

  const get = useCallback(
    (ccId: number) => {
      requestedCcIdRef.current = ccId;
      setIsLoading(true);
      getAhuParams(ccId);
    },
    [getAhuParams],
  );

  return { get, isLoading };
};

export const useAhuParams = ({
  fetch = true,
  includeExtraData = true,
  construction_class_id = 1,
} = {}) => {
  const params = useSelector((state: IState) => state.ahu.ahu_params);
  const dispatch = useDispatch();

  const { merge, ready } = useAhuExtraData(
    construction_class_id,
    includeExtraData,
  );

  const [getAhuParams, { isFetching }] = useLazyGetAhuParamsQuery();

  const get = useCallback(
    async (ccId: number) => {
      const res = await getAhuParams(ccId).unwrap();
      const merged = merge ? merge(res) : res;
      dispatch(setAhuParams(merged));
    },
    [getAhuParams, merge, dispatch],
  );

  useEffect(() => {
    if (fetch) {
      get(construction_class_id);
    }
  }, [fetch, construction_class_id, get]);

  useEffect(() => {
    if (
      !params ||
      !includeExtraData ||
      !ready ||
      params.construction_method != null
    ) {
      return;
    }

    dispatch(setAhuParams(merge(params)));
  }, [params, includeExtraData, ready, merge, dispatch]);

  return { params, isLoading: isFetching, get };
};

export const useConstructionClasses = () => {
  const dispatch = useDispatch();
  const { data, isLoading } = useGetConstructionClassesQuery();

  useEffect(() => {
    data && dispatch(setConstructionClasses(data));
  }, [data]);

  return { data, isLoading };
};

export const useGetAhuById = (id: string | undefined, fetch = true) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [getAhu, { data: ahu, isLoading, error }] = useLazyGetAhuByIdQuery();

  useEffect(() => {
    //@ts-ignore
    if (error?.status === 404) navigate('/dashboard');
  }, [error]);

  useEffect(() => {
    fetch && id && getAhu(id);
  }, [id]);

  useEffect(() => {
    if (ahu) {
      dispatch(setAhu(ahu));
      dispatch(setCurrentProjectId(ahu.project_id));
    }
  }, [ahu]);

  return { ahu, isLoading, get: getAhu };
};

export const useGetComponents = () => {
  const dispatch = useDispatch();
  const { data: components, isLoading } = useGetComponentsQuery();

  useEffect(() => {
    components && dispatch(setComponents(components));
  }, [components]);

  return { components, isLoading };
};

export const useGetAccessories = () => {
  const dispatch = useDispatch();
  const { data: accessories, isLoading } = useGetAccessoriesQuery();

  useEffect(() => {
    accessories && dispatch(setAccessories(accessories));
  }, [accessories]);

  return { accessories, isLoading };
};

export const useGetSectionAccessories = (section_id: number) => {
  const dispatch = useDispatch();
  const [getAccessories, { data: accessories, isLoading }] =
    useLazyGetSectionAccessoriesQuery();

  useEffect(() => {
    getAccessories(section_id);
  }, [section_id]);

  useEffect(() => {
    accessories && dispatch(setSectionAccessories({ accessories, section_id }));
  }, [accessories]);

  return { accessories, isLoading };
};

export const useGetAhuTunnels = (id: number, fetch = true) => {
  const dispatch = useDispatch();
  const [getAhuTunnels, { data: tunnels, isLoading }] =
    useLazyGetAhuTunnelsQuery();

  useEffect(() => {
    fetch && getAhuTunnels(id);
  }, [id]);

  useEffect(() => {
    if (tunnels) dispatch(setAhuTunnels({ tunnels, id }));
  }, [tunnels]);

  return { tunnels, isLoading, get: getAhuTunnels };
};

export const useGetAhuComponents = (id: number, fetch = true) => {
  const dispatch = useDispatch();
  const [getAhuComponents, { data: components, isLoading }] =
    useLazyGetAhuComponentsQuery();

  useEffect(() => {
    fetch && getAhuComponents(id);
  }, [id]);

  useEffect(() => {
    if (components) dispatch(setAhuComponents(components));
  }, [components]);

  return { components, isLoading, get: getAhuComponents };
};

export const useGetTunnelCasing = (id: number, fetch = true) => {
  const dispatch = useDispatch();
  const [getTunnelCasing, { data: casing, isLoading }] =
    useLazyGetTunnelCasingQuery();

  useEffect(() => {
    fetch && getTunnelCasing(id);
  }, [id]);

  useEffect(() => {
    if (casing) dispatch(setTunnelCasing({ casing, id }));
  }, [casing]);

  return { casing, isLoading, get: getTunnelCasing };
};

export const useGetTunnelFloor = (
  id: number,
  tunnel_id: number,
  fetch = true,
) => {
  const dispatch = useDispatch();
  const [getTunnelFloor, { data: floor, isLoading }] =
    useLazyGetTunnelFloorQuery();

  useEffect(() => {
    fetch && getTunnelFloor(id);
  }, [id]);

  useEffect(() => {
    if (floor) dispatch(setTunnelFloor({ floor, id: tunnel_id }));
  }, [floor]);

  return { floor, isLoading, get: getTunnelFloor };
};

export const useGetAhuCertifications = (id: number, fetch = true) => {
  const dispatch = useDispatch();
  const [getAhuCertifications, { data: certifications, isLoading }] =
    useLazyGetAhuCertificationsQuery();

  useEffect(() => {
    fetch && getAhuCertifications(id);
  }, [id]);

  useEffect(() => {
    if (certifications) {
      dispatch(setAhuCertifications({ certifications, id }));
    }
  }, [certifications, dispatch, id]);

  return { certifications, isLoading, get: getAhuCertifications };
};

export const useGetVoltages = () => {
  const dispatch = useDispatch();
  const { data: voltages, isLoading } = useGetVoltagesQuery();

  useEffect(() => {
    if (voltages) {
      dispatch(setVoltages(voltages));
    }
  }, [voltages]);

  return { voltages, isLoading };
};

export const useGetTunnelIndexes = (fetch = true) => {
  const dispatch = useDispatch();
  const [getIndexes, { data: indexes, isLoading }] =
    useLazyGetTunnelIndexesQuery();

  useEffect(() => {
    fetch && getIndexes();
  }, []);

  useEffect(() => {
    if (indexes) {
      dispatch(setTunnelsIndexes(indexes));
    }
  }, [indexes]);

  return { indexes, isLoading, get: getIndexes };
};
export const useGetRoofLayers = (fetch = true) => {
  const dispatch = useDispatch();
  const [getRoofLayers, { data: roofLayers, isLoading }] =
    useLazyGetRoofLayersQuery();

  useEffect(() => {
    fetch && getRoofLayers();
  }, []);

  useEffect(() => {
    if (roofLayers) {
      dispatch(setRoofLayers(roofLayers));
    }
  }, [roofLayers]);

  return { roofLayers, isLoading, get: getRoofLayers };
};

export const useGetRoofShapes = (fetch = true) => {
  const dispatch = useDispatch();
  const [getRoofShapes, { data: roofShapes, isLoading }] =
    useLazyGetRoofShapesQuery();

  useEffect(() => {
    fetch && getRoofShapes();
  }, []);

  useEffect(() => {
    if (roofShapes) {
      dispatch(setRoofShapes(roofShapes));
    }
  }, [roofShapes]);

  return { roofShapes, isLoading, get: getRoofShapes };
};

export const useGetRoofSeamDirections = (fetch = true) => {
  const dispatch = useDispatch();
  const [getRoofSeamDirections, { data: roofSeamDirections, isLoading }] =
    useLazyGetRoofShapesQuery();

  useEffect(() => {
    fetch && getRoofSeamDirections();
  }, []);

  useEffect(() => {
    if (roofSeamDirections) {
      dispatch(setRoofSeamDirections(roofSeamDirections));
    }
  }, [roofSeamDirections]);

  return { roofSeamDirections, isLoading, get: getRoofSeamDirections };
};

export const useGetRoofDirections = () => {
  const dispatch = useDispatch();
  const [getRoofDirections, { data: roofDirections, isLoading }] =
    useLazyGetRoofSlopeDirectionQuery();

  useEffect(() => {
    if (roofDirections) {
      dispatch(setRoofDirections(roofDirections));
    }
  }, [roofDirections]);

  return { roofDirections, isLoading, getRoofDirections };
};

export const useGetRoofOrientation = () => {
  const dispatch = useDispatch();
  const [getRoofOrientation, { data: roofOrientation, isLoading }] =
    useLazyGetRoofSlopeOrinetationQuery();

  useEffect(() => {
    if (roofOrientation) {
      dispatch(setRoofOrientation(roofOrientation));
    }
  }, [roofOrientation]);

  return { roofOrientation, isLoading, getRoofOrientation };
};

export const useGetSpecialOptions = (ahu_id?: string, fetch = true) => {
  const dispatch = useDispatch();
  const [getSpecialOptions, { data: options, isLoading }] =
    useLazyGetSpecialOptionsQuery();

  useEffect(() => {
    fetch && getSpecialOptions(ahu_id);
  }, []);

  useEffect(() => {
    if (options) {
      dispatch(setSpecialOptions({ options, key: ahu_id || 'all' }));
    }
  }, [options]);

  return { options, isLoading, get: getSpecialOptions };
};

export const useGetTunnelSections = (
  tunnel_id: number,
  fetch = true,
  selectSection = false,
) => {
  const dispatch = useDispatch();
  const { selected_section } = useSelector((state: IState) => state.ahu);
  const [getSections, { data: sections, isLoading }] =
    useLazyGetTunnelSectionsQuery();

  useEffect(() => {
    fetch && getSections(tunnel_id);
  }, []);

  useEffect(() => {
    if (sections) {
      dispatch(setTunnelSections({ sections, id: tunnel_id }));
      if (selectSection && !selected_section && sections[0])
        dispatch(setSelectedSection(sections[0]));
    }
  }, [sections]);

  return { sections, isLoading, get: getSections };
};

export const useGetSpecialOptionsSettings = (
  ahu_id: number | string,
  fetch = true
) => {
  const dispatch = useDispatch();
  const [getSpecialOptionsSettings, { data: settings, isLoading }] =
    useLazyGetSpecialOptionsSettingsQuery();

  useEffect(() => {
    // The catalog is the AHU's construction class's, so there is nothing to
    // ask for until we know which AHU.
    if (fetch && ahu_id) getSpecialOptionsSettings(ahu_id);
  }, [ahu_id]);

  useEffect(() => {
    if (settings) {
      dispatch(setSpecialOptionsSettings(settings));
    }
  }, [settings]);

  return { settings, isLoading, get: getSpecialOptionsSettings };
};

export const useGetDmounts = (section_id: number, fetch = true) => {
  const dispatch = useDispatch();
  const [getDmounts, { data: dmounts, isLoading }] = useLazyGetDMountsQuery();

  useEffect(() => {
    fetch && getDmounts(section_id);
  }, []);

  useEffect(() => {
    if (dmounts) {
      dispatch(setDmounts({ dmounts, section_id }));
    }
  }, [dmounts]);

  return { dmounts, isLoading, get: getDmounts };
};

export const useGetInternalWalls = (section_id: number) => {
  const dispatch = useDispatch();
  const [getHorizontalWalls, { data: horizontal_internal_walls, isLoading }] =
    useLazyGetHorizontalInternalWallsQuery();

  const [
    getVerticalWalls,
    { data: vertical_internal_walls, isLoading: loading },
  ] = useLazyGetVerticalInternalWallsQuery();

  useEffect(() => {
    getHorizontalWalls(section_id);
    getVerticalWalls(section_id);
  }, []);

  useEffect(() => {
    if (horizontal_internal_walls && vertical_internal_walls) {
      dispatch(
        setInternalWalls({
          internalWalls: [
            ...vertical_internal_walls,
            ...horizontal_internal_walls,
          ],
          section_id,
        }),
      );
    }
  }, [horizontal_internal_walls, vertical_internal_walls]);

  return {
    walls: [],
    isLoading: loading || isLoading,
    getHorizontalWalls,
    getVerticalWalls,
  };
};

export const useGetRatedVa = (ahu_id: number) => {
  const dispatch = useDispatch();
  const { data: rated24, isLoading: rated24Loading } =
    useGetAhuRatedVa24Query(ahu_id);
  const { data: rated120, isLoading: rated120Loading } =
    useGetAhuRatedVa120Query(ahu_id);

  useEffect(() => {
    if (rated24 && rated120) {
      dispatch(setRatedVA({ rated24, rated120, ahu_id }));
    }
  }, [rated24, rated120]);

  return { rated24, rated120, isLoading: rated24Loading || rated120Loading };
};

export const useGetAccessoryPosition = (id: number) => {
  const dispatch = useDispatch();
  const [getPosition, { data: position, isLoading }] =
    useLazyGetAccessoryPositionQuery();

  useEffect(() => {
    getPosition(id);
  }, [id]);

  useEffect(() => {
    if (position) dispatch(setAccessoryPosition({ id, position }));
  }, [position]);

  return { position, isLoading, getPosition };
};

export const useGetCount = (str: string, query: any) => {
  const [count, setCount] = useState<number | null>(null);
  const [getCount, { data, isLoading }] = query();

  useEffect(() => {
    if (str) getCount(str);
  }, [str]);

  useEffect(() => {
    if (data) setCount(data.count);
  }, [data]);

  return { isLoading, count, get: getCount };
};

export const useGetAllCount = (query: any) => {
  const { data, isLoading } = query();

  return { isLoading, count: data?.count || null };
};

export const useGetAccessoriesAvaibleSections = () => {
  const dispatch = useDispatch();
  const { data: uvc } = useGetUvcAvaibleSectionsQuery();
  const { data: drain_pan } = useGetDrainPanAvaibleSectionsQuery();

  useEffect(() => {
    if (drain_pan && uvc) {
      dispatch(setAccessoriesAvaibleSections({ uvc, drain_pan }));
    }
  }, [uvc, drain_pan]);
};
